import { useEffect, useMemo, useState } from "react";
import { ReportFieldTree } from "../components/ReportFieldTree";
import { useNavigate, useParams } from "react-router-dom";

import { FilterRowEditor } from "../components/FilterRowEditor";
import {
  useV1ReportingDefinitionsCreate as useReportDefinitionCreate,
  useV1ReportingDefinitionsRetrieve as useReportDefinitionRetrieve,
  useV1ReportingDefinitionsPartialUpdate as useReportDefinitionUpdate,
  useV1ReportingSourcesList as useReportSourcesList,
} from "@/api/generated/v1/v1";
import type { ReportFilter, FieldReference } from "../api/types";
import { PageBanner } from "@/shared/components/PageBanner";

/**
 * ADR-096 Rule RPT-08: انتخاب Source، انتخاب فیلد، فیلتر builder.
 * تمام گزینه‌های source/field از رجیستری بک‌اند می‌آید (Rule RPT-01) —
 * هیچ مدل یا فیلدی اینجا hardcode نمی‌شود.
 */
export function ReportBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const { data: sourcesData } = useReportSourcesList();
  const sources = (Array.isArray(sourcesData) ? sourcesData : (sourcesData as any)?.data) ?? [];

  const { data: existing } = useReportDefinitionRetrieve(id ?? "", { query: { enabled: !!id } });
  const createMutation = useReportDefinitionCreate();
  const updateMutation = useReportDefinitionUpdate();

  const [name, setName] = useState("");
  const [sourceKey, setSourceKey] = useState("");
  const [selectedFields, setSelectedFields] = useState<FieldReference[]>([]);
  const [filters, setFilters] = useState<ReportFilter[]>([]);
  const [groupBy, setGroupBy] = useState<FieldReference[]>([]);
  const [isShared, setIsShared] = useState(false);

  useEffect(() => {
    const def = existing as any;
    if (def) {
      setName(def.name);
      setSourceKey(def.source_key);
      setSelectedFields(def.selected_fields ?? []);
      setFilters(def.filters ?? []);
      setGroupBy(def.group_by ?? []);
      setIsShared(def.is_shared ?? false);
    }
  }, [existing]);

  const currentSource = useMemo(
    () => sources.find((s: any) => s.key === sourceKey),
    [sources, sourceKey]
  );

  function toggleField(fieldRef: FieldReference) {
    setSelectedFields((prev) => {
      const exists = prev.find(f => f.field === fieldRef.field && (f.path || []).join('.') === (fieldRef.path || []).join('.'));
      if (exists) {
        return prev.filter(f => f !== exists);
      }
      return [...prev, fieldRef];
    });
  }

  function toggleGroupBy(fieldRef: FieldReference) {
    setGroupBy((prev) => {
      const exists = prev.find(f => f.field === fieldRef.field && (f.path || []).join('.') === (fieldRef.path || []).join('.'));
      if (exists) {
        return prev.filter(f => f !== exists);
      }
      return [...prev, fieldRef];
    });
  }
  
  function updateAlias(index: number, alias: string) {
    setSelectedFields((prev) => {
      const next = [...prev];
      const item = next[index];
      if (item) {
        next[index] = { path: item.path, field: item.field, alias };
      }
      return next;
    });
  }

  function addFilter() {
    const firstField = selectedFields[0];
    if (!firstField) return;
    setFilters((prev) => [...prev, { path: firstField.path, field: firstField.field, op: "eq", value: "" }]);
  }

  async function handleSubmit() {
    const payload = {
      name,
      source_key: sourceKey,
      selected_fields: selectedFields,
      filters,
      group_by: groupBy,
      is_shared: isShared,
    };

    if (isEdit && id) {
      await updateMutation.mutateAsync({ id, data: payload });
      navigate(`/reporting/${id}/run`);
    } else {
      const created: any = await createMutation.mutateAsync({ data: payload as any });
      const newId = created?.id ?? created?.data?.id;
      navigate(`/reporting/${newId}/run`);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const canSubmit = name.trim().length > 0 && sourceKey && selectedFields.length > 0;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={isEdit ? "ویرایش گزارش" : "طراحی گزارش جدید"}
        subtitle="تعریف منابع داده، فیلدها و فیلترهای گزارش"
        icon="🛠️"
        helpLink="/help/reporting/builder"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "1000px", margin: "0 auto", padding: "2rem", width: "100%" }}>
        <div className="table-container" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
            <tbody>
              <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
              <th className="p-4 align-top w-1/4 font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                نام گزارش
              </th>
              <td className="p-4 align-top w-3/4">
                <input
                  className="w-full max-w-md rounded border px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </td>
            </tr>

            <tr className="border-b border-gray-200">
              <th className="p-4 align-top font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                منبع داده (Source)
              </th>
              <td className="p-4 align-top">
                <select
                  className="w-full max-w-md rounded border px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  value={sourceKey}
                  onChange={(e) => {
                    setSourceKey(e.target.value);
                    setSelectedFields([]);
                    setFilters([]);
                    setGroupBy([]);
                  }}
                >
                  <option value="">انتخاب کنید...</option>
                  {sources.map((s: any) => (
                    <option key={s.key} value={s.key}>
                      {s.verbose_name}
                    </option>
                  ))}
                </select>
              </td>
            </tr>

            {currentSource && (
              <>
                <tr className="border-b border-gray-200">
                  <th className="p-4 align-top font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                    فیلدها (انتخاب و JOIN)
                  </th>
                  <td className="p-4 align-top">
                    <div className="rounded-md border p-4 bg-gray-50 shadow-sm max-w-3xl">
                      <ReportFieldTree
                        source={currentSource}
                        allSources={sources}
                        path={[]}
                        depth={0}
                        selectedFields={selectedFields}
                        onToggleField={toggleField}
                      />
                    </div>
                  </td>
                </tr>

                {selectedFields.length > 0 && (
                  <tr className="border-b border-gray-200">
                    <th className="p-4 align-top font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                      عناوین نمایشی (Alias)
                    </th>
                    <td className="p-4 align-top">
                      <div className="space-y-2 max-w-2xl">
                        {selectedFields.map((sf, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-white p-2 border rounded shadow-sm">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded border border-blue-200" dir="ltr">
                              {sf.path ? sf.path.join('.') + '.' : ''}{sf.field.replace(/__/g, " - ").replace(/_/g, " ")}
                            </span>
                            <input
                              className="rounded border px-2 py-1 text-sm flex-1 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                              placeholder="عنوان پیش‌فرض..."
                              value={sf.alias || ""}
                              onChange={(e) => updateAlias(idx, e.target.value)}
                            />
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}

                <tr className="border-b border-gray-200">
                  <th className="p-4 align-top font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                    <div className="flex flex-col gap-2 items-start">
                      <span>فیلترها</span>
                      <button
                        type="button"
                        onClick={addFilter}
                        disabled={selectedFields.length === 0}
                        className="text-sm px-2 py-1 bg-white border rounded text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                      >
                        + افزودن فیلتر
                      </button>
                    </div>
                  </th>
                  <td className="p-4 align-top">
                    <div className="space-y-2 max-w-3xl">
                      {filters.map((f, idx) => (
                        <FilterRowEditor
                          key={idx}
                          availableFields={selectedFields}
                          filter={f}
                          onChange={(next) =>
                            setFilters((prev) => prev.map((p, i) => (i === idx ? next : p)))
                          }
                          onRemove={() => setFilters((prev) => prev.filter((_, i) => i !== idx))}
                        />
                      ))}
                      {filters.length === 0 && (
                        <p className="text-xs text-gray-500 mt-2">فیلتری تعریف نشده — همهٔ ردیف‌ها اعمال می‌شود.</p>
                      )}
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-gray-200">
                  <th className="p-4 align-top font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                    گروه‌بندی (اختیاری)
                  </th>
                  <td className="p-4 align-top">
                    <div className="rounded-md border p-4 bg-gray-50 shadow-sm max-w-3xl">
                      <ReportFieldTree
                        source={currentSource}
                        allSources={sources}
                        path={[]}
                        depth={0}
                        selectedFields={groupBy}
                        onToggleField={toggleGroupBy}
                      />
                    </div>
                  </td>
                </tr>

                <tr>
                  <th className="p-4 align-top font-medium text-gray-700 bg-gray-50 border-l border-gray-200">
                    تنظیمات دسترسی
                  </th>
                  <td className="p-4 align-top">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={isShared}
                        onChange={(e) => setIsShared(e.target.checked)}
                      />
                      اشتراک‌گذاری با کل tenant
                    </label>
                  </td>
                </tr>
              </>
            )}
            </tbody>
          </table>
        </div>

        <div className="flex-end pt-6 mt-6 border-t" style={{ borderColor: "var(--glass-border)" }}>
          <button
            type="button"
            disabled={!canSubmit || isSaving}
            onClick={handleSubmit}
            className="btn btn-primary"
            style={{ minWidth: "150px" }}
          >
            {isSaving ? "در حال ذخیره..." : "ذخیره و اجرا"}
          </button>
        </div>
      </div>
    </div>
  );
}
